import { mvpApi } from "../../services/api";

export type UploadCaseDocumentOptions = {
  coverageDateFrom?: string;
  coverageDateTo?: string;
  residencyDocLabel?: string;
  /** For self-employment uploads; stored with the uploaded document record. */
  selfEmploymentActivity?: string;
  /** Stored on Mongo (max 100 chars server-side); use for criminal certificate country label. */
  supportingDescription?: string;
  /** Explicit field for criminal record country (preferred over generic description). */
  criminalRecordCountry?: string;
};

export type UploadCaseDocumentResult =
  | { ok: true; documentId?: string; sectionId?: string }
  | { ok: false; message: string; retryable?: boolean };

function isLikelyTransientUploadFailure(err: unknown): boolean {
  const e = err as {
    response?: { status?: number };
    code?: string;
    message?: string;
  };
  const status = e.response?.status;
  if (e.response == null) {
    const code = String(e.code || "");
    if (["ECONNABORTED", "ERR_NETWORK", "ETIMEDOUT", "ECONNRESET"].includes(code)) return true;
    const msg = String(e.message || "").toLowerCase();
    return (
      msg.includes("network error") ||
      msg.includes("timeout") ||
      msg.includes("failed to fetch") ||
      msg.includes("load failed")
    );
  }
  if (status === 408 || status === 429) return true;
  return typeof status === "number" && status >= 500 && status <= 599;
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function uploadCaseDocumentFileOnce(
  ensureCaseSession: () => Promise<string | null>,
  documentType: string,
  file: File,
  opts: UploadCaseDocumentOptions = {}
): Promise<UploadCaseDocumentResult> {
  const sid = await ensureCaseSession();
  if (!sid) {
    return { ok: false, message: "Could not start your case session. Please try again." };
  }
  try {
    const res = await mvpApi.uploadDocument(sid, file, documentType, {
      coverageDateFrom: opts.coverageDateFrom,
      coverageDateTo: opts.coverageDateTo,
      residencyDocLabel: opts.residencyDocLabel,
      selfEmploymentActivity: opts.selfEmploymentActivity,
      supportingDescription: opts.supportingDescription,
      criminalRecordCountry: opts.criminalRecordCountry,
    });
    const data = res?.data ?? {};
    const documentId =
      data.document_id != null ? String(data.document_id).trim() : undefined;
    const sectionId =
      data.section_id != null ? String(data.section_id).trim() : undefined;
    return { ok: true, documentId: documentId || undefined, sectionId: sectionId || undefined };
  } catch (err: unknown) {
    const retryable = isLikelyTransientUploadFailure(err);
    const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
    const msg =
      typeof detail === "string"
        ? detail
        : "Upload failed. Allowed types: PDF, DOC, JPG, TIF, GIF - max 6 MB - check the file name.";
    return { ok: false, message: msg, retryable };
  }
}

/**
 * Uploads a file to the unified case session (S3 + Mongo) so advisors see it in Documents.
 */
export async function uploadCaseDocumentFile(
  ensureCaseSession: () => Promise<string | null>,
  documentType: string,
  file: File,
  opts: UploadCaseDocumentOptions = {}
): Promise<UploadCaseDocumentResult> {
  return uploadCaseDocumentFileOnce(ensureCaseSession, documentType, file, opts);
}

/**
 * Same as {@link uploadCaseDocumentFile}, but runs a second attempt after a short backoff when the first failure looks transient (network / timeout / 5xx).
 */
export async function uploadCaseDocumentFileWithRetry(
  ensureCaseSession: () => Promise<string | null>,
  documentType: string,
  file: File,
  opts: UploadCaseDocumentOptions = {}
): Promise<UploadCaseDocumentResult> {
  const first = await uploadCaseDocumentFileOnce(ensureCaseSession, documentType, file, opts);
  if (first.ok === true) {
    return first;
  }
  if (first.retryable === true) {
    await delay(700);
    return uploadCaseDocumentFileOnce(ensureCaseSession, documentType, file, opts);
  }
  return first;
}
